import {
    FetchAuctionBidsParams,
    FetchCampaignExpertsParams,
    FetchCampaignInvitesParams,
    FetchCampaignItemBidsParams,
    FetchCampaignItemsParams,
    FetchCampaignPromoCodesParams,
} from 'api/campaigns';
import { CustomFieldGroup } from 'types/domain';
import { FilterParams } from 'types/params';

const campaignQueryKeys = {
    campaign: (campaignId: number) => ['campaigns', campaignId],
    teams: (campaignId: number) => [...campaignQueryKeys.campaign(campaignId), 'teams'],
    teamsFiltered: (campaignId: number, params: FilterParams = {}) => [
        ...campaignQueryKeys.teams(campaignId),
        'teams',
        params,
    ],
    teamById: (campaignId: number, teamId: number) => [
        ...campaignQueryKeys.teams(campaignId),
        teamId,
    ],
    customFields: (campaignId: number) => [...campaignQueryKeys.teams(campaignId), 'customFields'],
    customFieldsFiltered: (campaignId: number, params: { group?: CustomFieldGroup } = {}) => [
        ...campaignQueryKeys.customFields(campaignId),
        params,
    ],
    members: (campaignId: number) => [...campaignQueryKeys.campaign(campaignId), 'members'],
    memberById: (campaignId: number, memberId: number) => [
        ...campaignQueryKeys.members(campaignId),
        memberId,
    ],
    membersFiltered: (campaignId: number, params: FilterParams = {}) => [
        ...campaignQueryKeys.members(campaignId),
        'members',
        params,
    ],
    items: (campaignId: number) => [...campaignQueryKeys.campaign(campaignId), 'items'],
    itemsFiltered: (
        campaignId: number,
        params: Omit<FetchCampaignItemsParams, 'campaignId'> = {},
    ) => [...campaignQueryKeys.items(campaignId), params],
    item: (campaignId: number, itemId: number) => [...campaignQueryKeys.items(campaignId), itemId],
    promoCodes: (campaignId: number) => [...campaignQueryKeys.campaign(campaignId), 'promoCodes'],
    promoCodesFiltered: (campaignId: number, params: FetchCampaignPromoCodesParams = {}) => [
        ...campaignQueryKeys.promoCodes(campaignId),
        params,
    ],
    promoCode: (campaignId: number, promoCodeId: number) => [
        ...campaignQueryKeys.promoCodes(campaignId),
        promoCodeId,
    ],
    sponsoredItem: (campaignId: number, sponsorId: number, templateId: number) => [
        ...campaignQueryKeys.items(campaignId),
        'sponsoredItem',
        sponsorId,
        templateId,
    ],
    itemBids: (
        campaignId: number,
        itemId: number,
        params: Omit<FetchCampaignItemBidsParams, 'campaignId' | 'itemId'> = {},
    ) => [...campaignQueryKeys.item(campaignId, itemId), 'bids', params],
    auction: (campaignId: number, auctionId: number) => [
        ...campaignQueryKeys.campaign(campaignId),
        'auctions',
        auctionId,
    ],
    auctionBids: (
        campaignId: number,
        auctionId: number,
        params: Omit<FetchAuctionBidsParams, 'auctionId' | 'campaignId'> = {},
    ) => [...campaignQueryKeys.auction(campaignId, auctionId), 'bids', params],
    auctionBidders: (campaignId: number, auctionId: number) => [
        ...campaignQueryKeys.auction(campaignId, auctionId),
        'auction-bidders',
    ],
    auctionBidderById: (campaignId: number, auctionId: number, bidderId: number) => [
        ...campaignQueryKeys.auctionBidders(campaignId, auctionId),
        bidderId,
    ],
    auctionBiddersWithFilters: (campaignId: number, auctionId: number, params: FilterParams) => [
        ...campaignQueryKeys.auctionBidders(campaignId, auctionId),
        params,
    ],
    auctionCategories: (campaignId: number, auctionId: number) => [
        ...campaignQueryKeys.auction(campaignId, auctionId),
        'categories',
    ],
    sponsors: (campaignId: number) => [...campaignQueryKeys.campaign(campaignId), 'sponsors'],
    sponsorTerms: (campaignId: number) => [
        ...campaignQueryKeys.sponsors(campaignId),
        'sponsors-terms',
    ],
    campaignSponsors: (campaignId: number, params: FilterParams = {}) => [
        ...campaignQueryKeys.sponsors(campaignId),
        'sponsors-accounts',
        params,
    ],
    campaignSponsoredItems: (campaignId: number, sponsorId: number, params: FilterParams = {}) => [
        ...campaignQueryKeys.campaignSponsors(campaignId),
        'sponsored-items',
        sponsorId,
        params,
    ],
    experts: (campaignId: number) => [...campaignQueryKeys.campaign(campaignId), 'experts'],
    expertsWithFilters: (campaignId: number, params: FetchCampaignExpertsParams = {}) => [
        ...campaignQueryKeys.experts(campaignId),
        params,
    ],
    invites: (campaignId: number) => [...campaignQueryKeys.campaign(campaignId), 'invites'],
    invitesWithFilters: (campaignId: number, params: FetchCampaignInvitesParams = {}) => [
        ...campaignQueryKeys.invites(campaignId),
        params,
    ],
    payoutOptions: (campaignId: number, tipPercentages: number[], availableBalance: number) => [
        ...campaignQueryKeys.campaign(campaignId),
        'payoutOptions',
        tipPercentages,
        availableBalance,
    ],
};

export { campaignQueryKeys };
